import React from "react";
import { X } from "lucide-react";

const NormalizedProductModal = ({ isOpen, onClose, data }) => {
    if (!isOpen || !data) return null;

    const {
        title,
        brand,
        category,
        attributes,
        pricing,
        availability,
        media,
        reviews,
    } = data;

    const InfoRow = ({ label, value }) => (
        <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
            <span className="text-sm text-gray-500">{label}</span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">
                {value || "-"}
            </span>
        </div>
    );

    const AttributeTag = ({ label, items }) => {
        if (!items || items.length === 0) return null;
        return (
            <div className="mb-2">
                <span className="text-xs text-gray-500 block mb-1">{label}</span>
                <div className="flex flex-wrap gap-1">
                    {items.map((item, idx) => (
                        <span
                            key={idx}
                            className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2 py-1 rounded"
                        >
                            {item}
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white line-clamp-1">
                        {title}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left Column: Image & Basic Info */}
                        <div className="space-y-6">
                            <div className="aspect-square bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden flex items-center justify-center">
                                {media?.primaryImage ? (
                                    <img
                                        src={media.primaryImage}
                                        alt={title}
                                        className="w-full h-full object-contain"
                                    />
                                ) : (
                                    <span className="text-gray-400 text-sm">No Image</span>
                                )}
                            </div>

                            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                                    Pricing & Availability
                                </h3>
                                <InfoRow
                                    label="Price"
                                    value={
                                        pricing?.basePriceUsd
                                            ? `$${pricing.basePriceUsd}`
                                            : "-"
                                    }
                                />
                                <InfoRow
                                    label="Discount"
                                    value={
                                        pricing?.discount?.percentage
                                            ? `${pricing.discount.percentage}%`
                                            : "-"
                                    }
                                />
                                <InfoRow
                                    label="Stock Status"
                                    value={availability?.status || "-"}
                                />
                                <InfoRow label="Brand" value={brand} />
                            </div>
                        </div>

                        {/* Right Column: Details & Attributes */}
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                                    Categorization
                                </h3>
                                <div className="bg-white dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700 rounded-xl p-4">
                                    <InfoRow
                                        label="Primary"
                                        value={category?.primary}
                                    />
                                    <InfoRow
                                        label="Style Category"
                                        value={attributes?.styleCategory}
                                    />
                                    <InfoRow
                                        label="Gender"
                                        value={attributes?.gender}
                                    />
                                </div>
                            </div>

                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                                    Detailed Attributes
                                </h3>
                                <div className="bg-white dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700 rounded-xl p-4 space-y-3">
                                    <InfoRow
                                        label="Primary Material"
                                        value={attributes?.primaryMaterial}
                                    />
                                    <AttributeTag
                                        label="Colors"
                                        items={attributes?.colors}
                                    />
                                    <AttributeTag
                                        label="Special Features"
                                        items={attributes?.specialFeatures}
                                    />
                                    <AttributeTag
                                        label="Occasions"
                                        items={attributes?.occasion}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NormalizedProductModal;
